import { useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { zodResolver } from "@hookform/resolvers/zod"
import { addDays, startOfDay } from "date-fns"
import { LoadingSpinner } from "@/components/loading-spinner"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { DatePicker, VISUAL_DATE_FORMAT } from "@/components/ui/date-picker"
import { Dialog, DialogContentCustom, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Form, FormField } from "@/components/ui/form"
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from "@/components/ui/radio-group"
import { DateOnly } from "@/lib/date-only"
import { BetterDocRemoteRequestStatus } from "@/modules/admin/better-doc/components/better-doc-remote-request-status"
import { MEDIC_SELECTION_DEFAULT_DURATION_DAYS } from "@/modules/admin/better-doc/constants/better-doc-constants"
import { useBetterDocFetch } from "@/modules/admin/better-doc/hooks/useBetterDocFetch"
import { collectBetterDocErrorMessages } from "@/modules/admin/better-doc/lib/collect-better-doc-error-messages"
import { isBetterDocTextPresent } from "@/modules/admin/better-doc/lib/format-better-doc-value"
import {
  BetterDocMedicSelectionSchema,
  betterDocMedicSelectionSchema,
} from "@/modules/admin/better-doc/schema/better-doc-medic-selection-schema"
import {
  BetterDocRemoteMedic,
  fetchBetterDocRemoteRecommendation,
} from "@/modules/admin/better-doc/services/betterDocRemoteRecommendationQuery"
import { useSelectBetterDocMedicMutation } from "@/modules/admin/better-doc/services/useSelectBetterDocMedicMutation"
import { BetterDocSessionActionOutcome } from "@/modules/admin/better-doc/types/BetterDocSessionActionOutcome"

type BetterDocMedicSelectionDialogProps = {
  sessionId: number
  recommendationId: string
  onClose: () => void
  onCompleted: (outcome: BetterDocSessionActionOutcome) => void
}

type SelectableMedic = Pick<BetterDocRemoteMedic, "selectionType" | "selectionId"> & {
  selectionKey: string
  label: string
}

type RecommendationRequest = {
  recommendationId: string
}

const fetchRecommendationForRequest = ({ recommendationId }: RecommendationRequest) =>
  fetchBetterDocRemoteRecommendation(recommendationId)

const collectSelectableMedics = (medics: ReadonlyArray<BetterDocRemoteMedic>): ReadonlyArray<SelectableMedic> =>
  medics.map((medic) => ({
    selectionType: medic.selectionType,
    selectionId: medic.selectionId,
    selectionKey: `${medic.selectionType}-${medic.selectionId}`,
    label: [medic.name, medic.workplace?.name, medic.workplace?.city].filter(isBetterDocTextPresent).join(" · "),
  }))

const buildDefaultMedicSelectionValues = (): BetterDocMedicSelectionSchema => {
  const startDate = startOfDay(new Date())
  return { selectionKey: "", startDate, endDate: addDays(startDate, MEDIC_SELECTION_DEFAULT_DURATION_DAYS) }
}

export const BetterDocMedicSelectionDialog = ({
  sessionId,
  recommendationId,
  onClose,
  onCompleted,
}: BetterDocMedicSelectionDialogProps) => {
  const { t } = useTranslation()
  const [recommendationRequest] = useState<RecommendationRequest>({ recommendationId })
  const recommendationState = useBetterDocFetch(recommendationRequest, fetchRecommendationForRequest)
  const [selectMedic, isSelecting] = useSelectBetterDocMedicMutation()
  const form = useForm<BetterDocMedicSelectionSchema>({
    resolver: zodResolver(betterDocMedicSelectionSchema),
    mode: "onChange",
    defaultValues: buildDefaultMedicSelectionValues(),
  })
  const { control, handleSubmit, formState, trigger } = form
  const selectableMedics =
    recommendationState.status === "loaded" && recommendationState.data.recommendation !== null
      ? collectSelectableMedics(recommendationState.data.recommendation.medics)
      : []

  const submitMedicSelection = ({ selectionKey, startDate, endDate }: BetterDocMedicSelectionSchema) => {
    const selectedMedic = selectableMedics.find((selectableMedic) => selectableMedic.selectionKey === selectionKey)

    if (selectedMedic === undefined) {
      return
    }

    selectMedic({
      variables: {
        input: {
          sessionId,
          selectionType: selectedMedic.selectionType,
          selectionId: selectedMedic.selectionId,
          startDate: new DateOnly(startDate).toString(),
          endDate: new DateOnly(endDate).toString(),
        },
      },
      onCompleted: (response) => {
        const { actionResult, errors } = response.de_selectBetterDocMedic
        onCompleted({ result: actionResult, errorMessages: collectBetterDocErrorMessages(errors) })
        onClose()
      },
    })
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose()
    }
  }

  const renderMedicChoice = () => {
    switch (recommendationState.status) {
      case "idle":
      case "loading":
        return <LoadingSpinner size={24} className="flex justify-center p-8" />
      case "failed":
        return (
          <Banner type="inline" variant="critical" data-testid="better-doc-admin-medic-selection-error">
            {t("admin.betterDoc.requestFailed")}
          </Banner>
        )
      case "loaded": {
        const { recommendation, status, httpStatusCode, errorReason, exchangeId } = recommendationState.data

        if (recommendation === null) {
          return (
            <BetterDocRemoteRequestStatus
              status={status}
              httpStatusCode={httpStatusCode}
              errorReason={errorReason}
              exchangeId={exchangeId}
            />
          )
        }

        if (selectableMedics.length === 0) {
          return (
            <p className="text-on-surface-subdued text-sm" data-testid="better-doc-admin-medic-selection-empty">
              {t("admin.betterDoc.medicSelectionDialog.noRecommendedMedic")}
            </p>
          )
        }

        return (
          <FormField
            control={control}
            name="selectionKey"
            render={({ field }) => (
              <RadioGroup
                value={field.value}
                onValueChange={field.onChange}
                aria-label={t("admin.betterDoc.medicSelectionDialog.medic")}
                data-testid="better-doc-admin-medic-selection-medics">
                {selectableMedics.map((selectableMedic) => (
                  <RadioGroupItem
                    key={selectableMedic.selectionKey}
                    id={`better-doc-medic-selection-medic-${selectableMedic.selectionKey}`}
                    value={selectableMedic.selectionKey}
                    data-testid={`better-doc-admin-medic-selection-medic-${selectableMedic.selectionKey}`}>
                    <RadioGroupLabel htmlFor={`better-doc-medic-selection-medic-${selectableMedic.selectionKey}`}>
                      {selectableMedic.label}
                    </RadioGroupLabel>
                  </RadioGroupItem>
                ))}
              </RadioGroup>
            )}
          />
        )
      }
    }
  }

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContentCustom variant="small" data-testid="better-doc-admin-medic-selection-dialog">
        <DialogHeader>
          <DialogTitle>{t("admin.betterDoc.medicSelectionDialog.title", { sessionId })}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={handleSubmit(submitMedicSelection)}>
            <div className="flex flex-col gap-4 px-6 pb-2">
              <p className="text-on-surface-subdued text-sm">{t("admin.betterDoc.medicSelectionDialog.hint")}</p>
              {renderMedicChoice()}
              <FormField
                control={control}
                name="startDate"
                render={({ field, fieldState }) => (
                  <DatePicker
                    label={t("admin.betterDoc.medicSelectionDialog.startDate")}
                    dataTestId="better-doc-admin-medic-selection-start-date"
                    readOnly={false}
                    placeholder={VISUAL_DATE_FORMAT}
                    value={field.value}
                    enableReset={false}
                    onSelect={(selectedDate) => {
                      field.onChange(selectedDate)
                      void trigger("endDate")
                    }}
                    onReset={() => field.onChange(undefined)}
                    error={fieldState.error?.message}
                    translateError
                  />
                )}
              />
              <FormField
                control={control}
                name="endDate"
                render={({ field, fieldState }) => (
                  <DatePicker
                    label={t("admin.betterDoc.medicSelectionDialog.endDate")}
                    dataTestId="better-doc-admin-medic-selection-end-date"
                    readOnly={false}
                    placeholder={VISUAL_DATE_FORMAT}
                    value={field.value}
                    enableReset={false}
                    onSelect={field.onChange}
                    onReset={() => field.onChange(undefined)}
                    error={fieldState.error?.message}
                    translateError
                  />
                )}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="tertiary"
                onClick={onClose}
                data-testid="better-doc-admin-medic-selection-cancel-button">
                {t("common.button.cancel")}
              </Button>
              <Button
                type="submit"
                isLoading={isSelecting}
                disabled={!formState.isValid || isSelecting}
                data-testid="better-doc-admin-medic-selection-submit-button">
                {t("common.button.send")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContentCustom>
    </Dialog>
  )
}
